import { forwardRef, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

import type { InputHTMLAttributes, ReactNode } from 'react';

import styles from './InputField.module.css';

export interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'style'> {
  /** The variant to render. */
  variant?: 'large';
  /** The width to render. */
  width?: 'small' | 'compact';
  /** The type of the input element. */
  type?: 'text' | 'password' | 'email';
  /** The background color of the input. InputField is by default transparent. */
  background?: 'white';
  /** The id of the input element. */
  id: string;
  /** An element that represents a clear button. */
  clearTrigger?: ReactNode;
  /** `true` to color border red to show not valid; otherwise, `false`. */
  notValid?: boolean;
  /** The callback that is invoked when the clear button is triggered. */
  onClear?: () => void;
}

/** Represents a component that renders an input field which can have a clear button. */
export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      autoComplete = 'off',
      id,
      value,
      variant,
      width,
      type = 'text',
      background,
      clearTrigger,
      notValid = false,
      onClear,
      ...restProps
    }: InputFieldProps,
    forwardedRef
  ) => {
    const [clearButtonWidth, setClearButtonWidth] = useState<number>(0);
    const clearButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      if (clearButtonRef.current) {
        setClearButtonWidth(clearButtonRef.current.offsetWidth);
      }
    }, [value]);

    const handleClearTriggerClick = () => {
      if (onClear && forwardedRef && 'current' in forwardedRef && forwardedRef.current) {
        onClear();
        forwardedRef.current.focus();
      }
    };

    return (
      <div className={styles['input-field-wrapper']}>
        <input
          {...restProps}
          autoComplete={autoComplete}
          ref={forwardedRef}
          id={id}
          className={clsx(styles['input-field'], {
            [styles['input-field--large']]: variant === 'large',
            [styles[`input-field--${width}`]]: width,
            [styles[`input-field--${background}`]]: background,
            [styles[`input-field--not-valid`]]: notValid,
          })}
          type={type}
          value={value}
          style={{ paddingRight: value && clearTrigger ? clearButtonWidth : undefined }}
        />
        {Boolean(value) && clearTrigger && (
          <button
            type="button"
            className={styles['input-field__clear-button']}
            onClick={handleClearTriggerClick}
            ref={clearButtonRef}
            aria-label={`clear trigger for ${id}`}
          >
            {clearTrigger}
          </button>
        )}
      </div>
    );
  }
);
